import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useI18n } from "../i18n";
import { useSiteContent } from "../hooks/useSiteContent";
import api from "../api";

// Fallback slaytlar — backend ayar yoksa kullanılır.
const FALLBACK_SLIDES = [
  "https://res.cloudinary.com/dlenwe68g/image/upload/v1782636444/uploads/demiray/f8235250759c44e589d2d41b3169bb72.jpg",
  "https://res.cloudinary.com/dlenwe68g/image/upload/v1782636671/uploads/demiray/95fe7931c1ac4913b306508b8003952f.jpg",
];

const Hero = () => {
  const { t, lang } = useI18n();
  const { content: rawContent } = useSiteContent();
  const content = lang === "tr" ? rawContent : {};
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.2]);
  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/settings/hero").then((res) => {
      if (res.data?.slides?.length) setSlides(res.data.slides);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  const onScrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-black"
      data-testid="hero-section"
    >
      <motion.div style={{ y, opacity }} className="absolute inset-0 will-change-transform">
        <AnimatePresence mode="sync">
          <motion.img
            key={idx}
            src={slides[idx]}
            alt={`Slide ${idx + 1}`}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-[120%] object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-[var(--bg)]" />
      </motion.div>

      <div className="absolute inset-0 bp-overlay opacity-30 pointer-events-none" />

      <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20 md:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl"
        >
          <div className="overline text-white/70 mb-6" data-testid="hero-overline">
            {content["hero.overline"] || t.hero.overline}
          </div>
          <h1
            className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light leading-[0.95] mb-8 whitespace-pre-line"
            data-testid="hero-title"
          >
            {content["hero.headline"] || t.hero.title}
          </h1>
          <p
            className="font-body text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed mb-10"
            data-testid="hero-subtitle"
          >
            {content["hero.subtitle"] || t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#portfolio" onClick={onScrollTo("portfolio")} className="btn-sharp btn-primary" data-testid="hero-cta-primary">
              {content["hero.cta_primary"] || t.hero.cta}
              <ArrowRight size={16} />
            </a>
            <a href="#contact" onClick={onScrollTo("contact")} className="btn-sharp text-white" data-testid="hero-cta-secondary">
              {content["hero.cta_secondary"] || t.hero.ctaSecondary}
            </a>
          </div>
        </motion.div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-6 lg:left-12 z-10 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-px transition-all ${i === idx ? "w-12 bg-white" : "w-6 bg-white/30 hover:bg-white/60"}`}
            aria-label={`Slide ${i + 1}`}
            data-testid={`hero-slide-${i}`}
          />
        ))}
      </div>

      <motion.a
        href="#about"
        onClick={onScrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-white/70 hover:text-white text-xs tracking-[0.3em] uppercase"
        data-testid="hero-scroll-indicator"
      >
        Scroll
        <span className="w-12 h-px bg-white/40" />
        <ArrowDown size={14} />
      </motion.a>
    </section>
  );
};

export default Hero;
